import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wallet, Send, Check } from "lucide-react";
import { toast } from "sonner";

const fmtUsd = (c) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

/**
 * Admin Payout Queue
 *
 * Lists every confirmed lesson booking with an outstanding coach payout.
 * Admin marks individual bookings paid (after manual ACH/Stripe transfer)
 * OR bulk-marks a coach's entire pending balance paid in one click.
 */
export default function AdminPayoutQueue() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("unpaid");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/lessons/admin/payouts?payout_status=${tab}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const markBooking = async (booking_id) => {
    if (!confirm("Mark this payout as DISBURSED to the coach?")) return;
    setBusy(true);
    try {
      await api.post(`/lessons/admin/payouts/${booking_id}/mark-paid`, { note: "Admin manual disbursement" });
      toast.success("Payout marked as paid");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const markAllForCoach = async (coach_user_id, coach_name) => {
    if (!confirm(`Mark ALL pending payouts for ${coach_name} as disbursed?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/lessons/admin/payouts/coach/${coach_user_id}/mark-all-paid`, { note: "Bulk disbursement" });
      toast.success(`Marked ${r.marked_paid} bookings paid`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const totalPending = (data.pending_by_coach || []).reduce((s, c) => s + c.amount_cents, 0);

  return (
    <div className="rounded-xl ink-card p-5" data-testid="admin-payout-queue">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase flex items-center gap-1.5">
            <Wallet className="h-3 w-3" /> Lesson Payout Queue
          </div>
          <h2 className="font-display font-bold text-xl text-white mt-1">
            {tab === "unpaid" ? fmtUsd(totalPending) : `${data.bookings.length} bookings`}
            <span className="text-sm text-white/50 ml-2 font-normal">
              {tab === "unpaid" ? "outstanding to coaches" : tab === "paid" ? "disbursed" : "all-time"}
            </span>
          </h2>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 p-0.5 bg-white/[0.02]">
          {["unpaid", "paid", "all"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-md transition ${tab === t ? "bg-cyan-400/20 text-cyan-200" : "text-white/50 hover:text-white"}`}
              data-testid={`payout-tab-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      {/* Pending by coach (bulk action) */}
      {tab === "unpaid" && (data.pending_by_coach || []).length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">Pending by coach</div>
          <div className="space-y-1.5">
            {data.pending_by_coach.map((c) => (
              <div key={c.coach_user_id} className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2"
                data-testid={`payout-coach-${c.coach_user_id}`}>
                <div>
                  <div className="font-bold text-white text-sm">{c.coach_name}</div>
                  <div className="text-[10px] text-white/50">{c.booking_count} booking{c.booking_count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-display font-black text-lg text-white">{fmtUsd(c.amount_cents)}</div>
                  <Button size="sm" disabled={busy} onClick={() => markAllForCoach(c.coach_user_id, c.coach_name)}
                    className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
                    data-testid={`payout-mark-coach-${c.coach_user_id}`}>
                    <Send className="h-3.5 w-3.5 mr-1" /> Mark all paid
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-booking rows */}
      <div className="rounded-lg overflow-hidden border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.04] text-[10px] tracking-[0.2em] font-bold uppercase text-white/40">
            <tr>
              <th className="text-left px-3 py-2">Coach</th>
              <th className="text-left px-3 py-2">Student</th>
              <th className="text-left px-3 py-2">Lesson</th>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-right px-3 py-2">Payout</th>
              <th className="text-right px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.bookings.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-white/40">No bookings in this view.</td></tr>
            )}
            {data.bookings.map((b) => (
              <tr key={b.booking_id} className="text-white/80 hover:bg-white/[0.02]" data-testid={`payout-row-${b.booking_id}`}>
                <td className="px-3 py-2 font-semibold text-white truncate max-w-[140px]">{b.coach_name}</td>
                <td className="px-3 py-2 truncate max-w-[140px]">{b.student_name}</td>
                <td className="px-3 py-2 truncate max-w-[140px]">{b.lesson_label}</td>
                <td className="px-3 py-2 font-mono-num">{new Date(b.slot_iso).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold text-white">{fmtUsd(b.coach_payout_cents)}</td>
                <td className="px-3 py-2 text-right">
                  {b.payout_status === "paid" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">
                      <Check className="h-3 w-3" /> Paid
                    </span>
                  ) : (
                    <Button size="sm" disabled={busy} onClick={() => markBooking(b.booking_id)}
                      variant="outline" className="h-7 border-white/15 text-white bg-white/5 hover:bg-white/10 text-[11px]"
                      data-testid={`payout-mark-${b.booking_id}`}>
                      Mark paid
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
